import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useQuery } from '@tanstack/react-query';
import { NumericField } from '@ota/design-system';
import { fetchPropertyTypes, fetchAmenities, fetchHouseRules } from '../api';

interface FilterState {
  starRatings: number[];
  propertyTypeCodes: string[];
  amenityCodes: string[];
  houseRuleCodes: string[];
  minPrice: string;
  maxPrice: string;
  minRating: number | null;
}

interface ListingSidebarProps {
  filters: FilterState;
  onChange: (next: Partial<FilterState>) => void;
  onClearAll: () => void;
  resultsCount?: number;
}

const PRICE_BUCKETS: Array<[label: string, min: number, max: number | null]> = [
  ['₹0 – ₹1,500',         0,    1500],
  ['₹1,500 – ₹3,000',     1500, 3000],
  ['₹3,000 – ₹5,500',     3000, 5500],
  ['₹5,500 – ₹8,000',     5500, 8000],
  ['₹8,000 – ₹15,000',    8000, 15000],
  ['₹15,000 – ₹30,000',   15000, 30000],
  ['₹30,000+',            30000, null],
];

const USER_RATINGS: Array<[label: string, value: number]> = [
  ['Excellent · 4.2+', 4.2],
  ['Very Good · 3.5+', 3.5],
  ['Good · 3.0+',      3.0],
];

export function ListingSidebar({ filters, onChange, onClearAll, resultsCount }: ListingSidebarProps) {
  const types = useQuery({ queryKey: ['property-types'], queryFn: fetchPropertyTypes });
  const amenities = useQuery({ queryKey: ['amenities-public'], queryFn: fetchAmenities });
  const houseRules = useQuery({ queryKey: ['house-rules-public'], queryFn: fetchHouseRules });

  const toggleStar = (n: number) => {
    onChange({
      starRatings: filters.starRatings.includes(n)
        ? filters.starRatings.filter((s) => s !== n)
        : [...filters.starRatings, n],
    });
  };

  const toggleType = (code: string) => {
    onChange({
      propertyTypeCodes: filters.propertyTypeCodes.includes(code)
        ? filters.propertyTypeCodes.filter((c) => c !== code)
        : [...filters.propertyTypeCodes, code],
    });
  };

  const toggleAmenity = (code: string) => {
    onChange({
      amenityCodes: filters.amenityCodes.includes(code)
        ? filters.amenityCodes.filter((c) => c !== code)
        : [...filters.amenityCodes, code],
    });
  };

  const toggleHouseRule = (code: string) => {
    onChange({
      houseRuleCodes: filters.houseRuleCodes.includes(code)
        ? filters.houseRuleCodes.filter((c) => c !== code)
        : [...filters.houseRuleCodes, code],
    });
  };

  const isPriceBucketActive = (min: number, max: number | null) => {
    return Number(filters.minPrice || 0) === min
      && Number(filters.maxPrice || 0) === (max ?? 0);
  };

  const setPriceBucket = (min: number, max: number | null) => {
    if (isPriceBucketActive(min, max)) {
      onChange({ minPrice: '', maxPrice: '' });
    } else {
      onChange({ minPrice: String(min), maxPrice: max == null ? '' : String(max) });
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
          Filters
        </Typography>
        <Button size="small" onClick={onClearAll} sx={{ minWidth: 0, p: 0 }}>
          Clear all
        </Button>
      </Stack>

      {resultsCount !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {resultsCount} {resultsCount === 1 ? 'result' : 'results'}
        </Typography>
      )}

      <FilterSection title="Price per night">
        <Stack spacing={0.25}>
          {PRICE_BUCKETS.map(([label, min, max]) => (
            <FormControlLabel
              key={label}
              control={
                <Checkbox
                  size="small"
                  checked={isPriceBucketActive(min, max)}
                  onChange={() => setPriceBucket(min, max)}
                />
              }
              label={<Typography variant="body2">{label}</Typography>}
              sx={{ ml: 0 }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <NumericField
            size="small"
            placeholder="Min"
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            value={filters.minPrice}
            onChange={(e) => onChange({ minPrice: e.target.value })}
          />
          <NumericField
            size="small"
            placeholder="Max"
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            value={filters.maxPrice}
            onChange={(e) => onChange({ maxPrice: e.target.value })}
          />
        </Stack>
      </FilterSection>

      <FilterSection title="Star category">
        <Stack spacing={0.25}>
          {[5, 4, 3].map((n) => (
            <FormControlLabel
              key={n}
              control={
                <Checkbox
                  size="small"
                  checked={filters.starRatings.includes(n)}
                  onChange={() => toggleStar(n)}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2">{n} star</Typography>
                  {Array.from({ length: n }).map((_, i) => (
                    <StarIcon key={i} sx={{ fontSize: 14, color: 'warning.main' }} />
                  ))}
                </Stack>
              }
              sx={{ ml: 0 }}
            />
          ))}
        </Stack>
      </FilterSection>

      <FilterSection title="User rating">
        <Stack spacing={0.25}>
          {USER_RATINGS.map(([label, value]) => (
            <FormControlLabel
              key={value}
              control={
                <Checkbox
                  size="small"
                  checked={filters.minRating === value}
                  onChange={() => onChange({ minRating: filters.minRating === value ? null : value })}
                />
              }
              label={<Typography variant="body2">{label}</Typography>}
              sx={{ ml: 0 }}
            />
          ))}
        </Stack>
      </FilterSection>

      <FilterSection title="Property type">
        <Stack spacing={0.25}>
          {(types.data ?? []).map((t) => (
            <FormControlLabel
              key={t.code}
              control={
                <Checkbox
                  size="small"
                  checked={filters.propertyTypeCodes.includes(t.code)}
                  onChange={() => toggleType(t.code)}
                />
              }
              label={<Typography variant="body2">{t.label}</Typography>}
              sx={{ ml: 0 }}
            />
          ))}
        </Stack>
      </FilterSection>

      {(amenities.data ?? []).length > 0 && (
        <FilterSection title="Amenities">
          <Stack spacing={0.25}>
            {(amenities.data ?? []).map((a) => (
              <FormControlLabel
                key={a.code}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.amenityCodes.includes(a.code)}
                    onChange={() => toggleAmenity(a.code)}
                  />
                }
                label={<Typography variant="body2">{a.label}</Typography>}
                sx={{ ml: 0 }}
              />
            ))}
          </Stack>
        </FilterSection>
      )}

      {(houseRules.data ?? []).length > 0 && (
        <FilterSection title="House rules">
          <Stack spacing={0.25}>
            {(houseRules.data ?? []).map((r) => (
              <FormControlLabel
                key={r.code}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.houseRuleCodes.includes(r.code)}
                    onChange={() => toggleHouseRule(r.code)}
                  />
                }
                label={<Typography variant="body2">{r.label}</Typography>}
                sx={{ ml: 0 }}
              />
            ))}
          </Stack>
        </FilterSection>
      )}
    </Stack>
  );
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>{title}</Typography>
      {children}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

export type { FilterState };
